import { useEffect, useState } from 'react'
import { Plus, Trash2, ChevronDown, ChefHat, BookOpen, X } from 'lucide-react'
import { recipesApi, type Recipe, type Ingredient } from '../services/api'
import IngredientAutocomplete from '../components/IngredientAutocomplete'
import axios from 'axios'
import toast from 'react-hot-toast'
import AccessDenied from '../components/AccessDenied'
import { useAuth } from '../contexts/useAuth'

export default function MyRecipes() {
    const [recipes, setRecipes] = useState<Recipe[]>([])
    const [loading, setLoading] = useState(true)
    const [expanded, setExpanded] = useState<string | null>(null)
    const [showCreate, setShowCreate] = useState(false)
    const [showAccessDenied, setShowAccessDenied] = useState(false)
    const { isAuthenticated, loading: authLoading } = useAuth()

    // Create form
    const [name, setName] = useState('')
    const [servings, setServings] = useState(4)
    const [instructions, setInstructions] = useState('')
    const [category, setCategory] = useState('plat')
    const [ingredients, setIngredients] = useState<Array<{ ingredient_name: string; quantity: string; unit: string }>>([
        { ingredient_name: '', quantity: '', unit: 'g' }
    ])

    useEffect(() => {
        if (authLoading) return

        let active = true
        Promise.resolve().then(() => {
            if (active) setLoading(true)
        })
        recipesApi.list('', '', true)
            .then(res => {
                if (active) setRecipes(res)
            })
            .catch((error) => {
                if (!active) return
                if (axios.isAxiosError(error) && error.response?.status === 401) {
                    setShowAccessDenied(true)
                    return
                }
                toast.error('Erreur')
            })
            .finally(() => {
                if (active) setLoading(false)
            })
        return () => {
            active = false
        }
    }, [isAuthenticated, authLoading])

    const addIngRow = () => setIngredients(prev => [...prev, { ingredient_name: '', quantity: '', unit: 'g' }])
    const updateIngRow = (i: number, field: string, val: string, ing?: Ingredient) => {
        setIngredients(prev => prev.map((row, idx) => {
            if (idx !== i) return row
            const patch: Partial<{ ingredient_name: string; quantity: string; unit: string }> = { [field]: val }
            if (ing?.preferred_unit) patch.unit = ing.preferred_unit
            return { ...row, ...patch }
        }))
    }
    const removeIngRow = (i: number) => setIngredients(prev => prev.filter((_, idx) => idx !== i))

    const handleCreate = async () => {
        if (!name.trim()) return toast.error('Nom requis')
        const validIngs = ingredients.filter(i => i.ingredient_name.trim() && i.quantity)
        if (validIngs.length === 0) return toast.error('Au moins 1 ingrédient requis')
        try {
            const recipe = await recipesApi.create({
                name,
                servings,
                instructions,
                is_personal: true,
                category,
                ingredients: validIngs.map(i => ({ ingredient_name: i.ingredient_name, quantity: parseFloat(i.quantity), unit: i.unit })),
            })
            setRecipes(prev => [recipe, ...prev])
            setShowCreate(false)
            setName(''); setServings(4); setInstructions(''); setIngredients([{ ingredient_name: '', quantity: '', unit: 'g' }])
            toast.success('Recette créée !')
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 401) {
                setShowAccessDenied(true)
                return
            }
            toast.error('Erreur création')
        }
    }

    const handleDelete = async (id: string) => {
        if (!confirm('Supprimer cette recette ?')) return
        try {
            await recipesApi.delete(id)
            setRecipes(prev => prev.filter(r => r.id !== id))
            toast.success('Recette supprimée')
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 401) {
                setShowAccessDenied(true)
                return
            }
            toast.error('Erreur suppression')
        }
    }

    const UNITS = ['g', 'kg', 'ml', 'l', 'piece', 'cnt', 'tbsp', 'tsp']

    if (showAccessDenied) {
        return (
            <AccessDenied
                title="Creation de recette reservee"
                description="Vous n'avez pas les droits pour creer des recettes personnelles en mode invite."
            />
        )
    }

    return (
        <div>
            <div className="page-header" style={{ justifyContent: 'space-between' }}>
                <h1>Mes recettes</h1>
                <button
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                        if (!isAuthenticated) {
                            setShowAccessDenied(true)
                            return
                        }
                        setShowCreate(s => !s)
                    }}
                >
                    <Plus size={16} /> Créer
                </button>
            </div>

            {/* Create form */}
            {showCreate && (
                <div className="card fade-in" style={{ padding: 16, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <h3>Nouvelle recette</h3>
                    <div className="form-group">
                        <label className="form-label">Nom du plat</label>
                        <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="Ma tarte..." />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <div className="form-group">
                            <label className="form-label">Portions de base</label>
                            <div className="stepper-full">
                                <button className="stepper-btn" onClick={() => setServings(s => Math.max(1, s - 1))}>−</button>
                                <span className="stepper-val">{servings}</span>
                                <button className="stepper-btn" onClick={() => setServings(s => s + 1)}>+</button>
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Catégorie</label>
                            <select className="select" value={category} onChange={e => setCategory(e.target.value)}>
                                <option value="plat">Plat</option>
                                <option value="dessert">Dessert</option>
                                <option value="entree">Entrée</option>
                            </select>
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Instructions (optionnel)</label>
                        <textarea className="input" style={{ minHeight: 80, resize: 'vertical' }} value={instructions} onChange={e => setInstructions(e.target.value)} placeholder="Étapes de préparation..." />
                    </div>

                    {/* Ingredients */}
                    <div className="form-group">
                        <label className="form-label">Ingrédients (pour {servings} personne{servings > 1 ? 's' : ''})</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {ingredients.map((row, i) => (
                                <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 2fr) 60px 80px auto', gap: 6, alignItems: 'center' }}>
                                    <IngredientAutocomplete 
                                        compact 
                                        placeholder="Ingrédient" 
                                        value={row.ingredient_name} 
                                        onChange={(val, ing) => updateIngRow(i, 'ingredient_name', val, ing)} 
                                    />
                                    <input className="input input-compact" type="number" placeholder="Qté" value={row.quantity} onChange={e => updateIngRow(i, 'quantity', e.target.value)} />
                                    <select className="select input-compact" value={row.unit} onChange={e => updateIngRow(i, 'unit', e.target.value)}>
                                        {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                                    </select>
                                    {i > 0 && (
                                        <button className="btn btn-danger btn-sm btn-icon" onClick={() => removeIngRow(i)}><X size={13} /></button>
                                    )}
                                </div>
                            ))}
                            <button className="btn btn-ghost btn-sm" onClick={addIngRow}><Plus size={14} /> Ajouter un ingrédient</button>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-ghost" onClick={() => setShowCreate(false)} style={{ flex: 1 }}>Annuler</button>
                        <button className="btn btn-primary" onClick={handleCreate} style={{ flex: 2 }}>Enregistrer</button>
                    </div>
                </div>
            )}

            {loading && [1, 2].map(i => <div key={i} className="skeleton" style={{ height: 70, borderRadius: 14, marginBottom: 8 }} />)}

            {!loading && recipes.length === 0 && !showCreate && (
                <div className="empty-state">
                    <BookOpen size={48} />
                    <h3>Aucune recette perso</h3>
                    <p>Créez vos propres recettes pour les utiliser dans vos listes</p>
                </div>
            )}

            {recipes.map((r, idx) => (
                <div
                    key={r.id}
                    className="card"
                    style={{
                        marginBottom: 10,
                        padding: 0,
                        overflow: 'hidden',
                        animation: `slideUp 0.35s ease ${idx * 0.05}s both`,
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', cursor: 'pointer' }} onClick={() => setExpanded(e => e === r.id ? null : r.id)}>
                        <ChefHat size={18} color="var(--primary-light)" />
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{r.name}</div>
                            <div style={{ fontSize: '0.78rem', color: 'var(--text-3)' }}>{r.servings} pers. · {r.recipe_ingredients.length} ingrédient{r.recipe_ingredients.length > 1 ? 's' : ''}</div>
                        </div>
                        <button className="btn btn-danger btn-sm btn-icon" onClick={e => { e.stopPropagation(); handleDelete(r.id) }}>
                            <Trash2 size={14} />
                        </button>
                        <ChevronDown size={16} color="var(--text-3)" style={{ transform: expanded === r.id ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }} />
                    </div>

                    {expanded === r.id && (
                        <div style={{ borderTop: '1px solid var(--border)', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }} className="fade-in">
                            {r.instructions && (
                                <p style={{ fontSize: '0.85rem', color: 'var(--text-2)' }}>{r.instructions}</p>
                            )}
                            <p className="form-label">Ingrédients</p>
                            {r.recipe_ingredients.map(ri => (
                                <div key={ri.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                                    <span>{ri.name}</span>
                                    <span style={{ color: 'var(--text-3)' }}>{ri.quantity % 1 === 0 ? ri.quantity : ri.quantity.toFixed(1)} {ri.unit}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    )
}
